/**
 * Botón flotante de los asistentes IA (Guía del portal, Analista de tickets, ...).
 *
 * Todos se apilan en un mismo "dock" fijo abajo a la derecha (un solo contenedor en `body`),
 * así nunca se enciman entre sí. Mientras haya al menos uno montado, `body[data-dock-ia]`
 * reserva espacio al final del `<main>` del layout (ver `index.css`) para que el botón no tape
 * el paginado de las tablas. Al hacer scroll el botón se compacta a solo ícono y se expande al
 * detenerse, al pasar el mouse o al enfocarlo con teclado.
 *
 * Para que no pase desapercibido: una línea recorre el contorno de la píldora sin parar (SVG con
 * `stroke-dashoffset`, velocidad constante en todo el borde) y un halo late suave cada pocos
 * segundos (`.boton-ia-pista` en `index.css`). Con `prefers-reduced-motion` queda el borde quieto.
 */
import { useEffect, useState, type ComponentType } from "react";
import { createPortal } from "react-dom";
import { IconTip } from "@/components/ui/icon-tip";
import { cn } from "@/lib/utils";

const DOCK_ID = "dock-asistentes-ia";
let montados = 0;

/** Crea (o reusa) el contenedor del dock y lleva la cuenta de botones montados. */
function useDock(): HTMLElement | null {
  const [dock, setDock] = useState<HTMLElement | null>(null);
  useEffect(() => {
    let el = document.getElementById(DOCK_ID);
    if (!el) {
      el = document.createElement("div");
      el.id = DOCK_ID;
      el.className =
        "pointer-events-none fixed bottom-4 right-4 z-40 flex flex-col-reverse items-end gap-2 sm:bottom-5 sm:right-5";
      document.body.appendChild(el);
    }
    montados += 1;
    document.body.setAttribute("data-dock-ia", "");
    setDock(el);
    return () => {
      montados -= 1;
      if (montados <= 0) {
        montados = 0;
        document.body.removeAttribute("data-dock-ia");
        document.getElementById(DOCK_ID)?.remove();
      }
    };
  }, []);
  return dock;
}

/** `true` mientras el usuario hace scroll (en la ventana o en cualquier contenedor). */
function useHaciendoScroll(pausaMs = 700): boolean {
  const [activo, setActivo] = useState(false);
  useEffect(() => {
    let t: ReturnType<typeof setTimeout> | undefined;
    const alScroll = () => {
      setActivo(true);
      clearTimeout(t);
      t = setTimeout(() => setActivo(false), pausaMs);
    };
    document.addEventListener("scroll", alScroll, { capture: true, passive: true });
    return () => {
      clearTimeout(t);
      document.removeEventListener("scroll", alScroll, { capture: true });
    };
  }, [pausaMs]);
  return activo;
}

export interface BotonFlotanteIAProps {
  icono: ComponentType<{ className?: string }>;
  /** Texto corto visible junto al ícono (desde `sm`). */
  texto: string;
  /** Texto del tooltip (ej. el nombre completo del agente). */
  tooltip: string;
  ariaLabel: string;
  onClick: () => void;
  /** Posición en el dock: el menor queda más abajo. */
  orden?: number;
  /** Si el panel que abre está abierto (para `aria-expanded`). */
  abierto?: boolean;
  /** Atajo de teclado que abre el panel (`aria-keyshortcuts`, p. ej. "Alt+K"). */
  atajo?: string;
}

export function BotonFlotanteIA({ icono: Icono, texto, tooltip, ariaLabel, onClick, orden = 0, abierto, atajo }: BotonFlotanteIAProps) {
  const dock = useDock();
  const compacto = useHaciendoScroll();
  if (!dock) return null;

  return createPortal(
    <div className="pointer-events-auto" style={{ order: orden }}>
      <IconTip label={tooltip} side="left">
        <button
          type="button"
          onClick={onClick}
          aria-label={ariaLabel}
          aria-haspopup="dialog"
          aria-expanded={abierto}
          aria-keyshortcuts={atajo}
          data-compacto={compacto || undefined}
          className={cn(
            "boton-ia-pista group relative inline-flex h-12 items-center rounded-full bg-background/95 px-3.5 text-[14px] font-semibold text-foreground",
            "shadow-[0_8px_24px_-10px_hsl(var(--primary)/0.55)] backdrop-blur transition-[box-shadow,transform] duration-200 supports-[backdrop-filter]:bg-background/85",
            "hover:-translate-y-0.5 hover:shadow-[0_12px_28px_-10px_hsl(var(--primary)/0.65)]",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
          )}
        >
          {/* rx = ry = mitad del alto (h-12 = 48px, menos el trazo): píldora, nunca elipse. */}
          <svg aria-hidden className="boton-ia-pista__svg">
            <rect className="boton-ia-pista__riel" x="1" y="1" rx="23" ry="23" pathLength={100} />
            <rect className="boton-ia-pista__linea" x="1" y="1" rx="23" ry="23" pathLength={100} />
          </svg>
          <Icono className="relative size-5 shrink-0 text-primary" />
          <span
            className={cn(
              "relative hidden overflow-hidden whitespace-nowrap transition-[max-width,opacity,margin] duration-300 ease-out sm:inline-block",
              compacto
                ? "ml-0 max-w-0 opacity-0 group-hover:ml-2 group-hover:max-w-[10rem] group-hover:opacity-100 group-focus-visible:ml-2 group-focus-visible:max-w-[10rem] group-focus-visible:opacity-100"
                : "ml-2 max-w-[10rem] opacity-100",
            )}
          >
            {texto}
          </span>
        </button>
      </IconTip>
    </div>,
    dock,
  );
}
